/**
 * Manager for creating and configuring gem meshes
 * Provides procedural gem generation with PBR materials
 */

import {
  Color3,
  Mesh,
  PBRMaterial,
  Quaternion,
  type Scene,
  VertexData,
} from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager";
import {
  ORIGINAL_GEM_INDICES,
  ORIGINAL_GEM_NORMALS,
  ORIGINAL_GEM_POSITIONS,
} from "./originalGemData";

/**
 * Material preset configuration for gems
 */
interface GemMaterialPreset {
  metallic: number;
  roughness: number;
  alpha: number;
  emissiveIntensity: number;
  iridescenceEnabled: boolean;
  iridescenceIntensity: number;
  refractionIntensity: number;
  environmentIntensity: number;
}

/**
 * Manager responsible for gem creation and configuration
 */
export class GemManager extends BaseManager {
  /**
   * Counter for generating unique gem IDs
   */
  private static gemCounter = 0;

  /**
   * Brilliant preset matching historical gem appearance values
   * Based on original gemPresets.ts brilliant configuration
   */
  private static readonly BRILLIANT_PRESET: GemMaterialPreset = {
    alpha: 0.9,
    emissiveIntensity: 0.3,
    environmentIntensity: 0.7,
    iridescenceEnabled: true,
    iridescenceIntensity: 0.4,
    metallic: 0.05,
    refractionIntensity: 0.8, // transparency equivalent
    roughness: 0.03,
  };

  private scene: Scene | null = null;

  /**
   * Set the scene for this manager
   * @param scene The Babylon.js scene
   */
  setScene(scene: Scene): void {
    this.scene = scene;
  }

  /**
   * Create a brilliant gem mesh with PBR material
   * @param scene The scene to create the gem in
   * @param options Optional configuration for theme behavior
   * @returns The created gem mesh
   */
  createBrilliantGem(
    scene?: Scene,
    options?: {
      followAppTheme?: boolean; // If true, gem will update with app theme changes
      fixedTheme?: string; // If set, gem uses this theme regardless of app
    },
  ): Mesh {
    const targetScene = scene || this.scene;
    if (!targetScene) {
      throw new Error("No scene available for gem creation");
    }

    // Create gem mesh using EXACT vertex data from original BrilliantGem.babylon
    // at commit db4f4f67^ (145 vertices, 64 faces)
    // Use unique name to avoid physics aggregate ID collisions
    const gemId = GemManager.gemCounter++;
    const gem = new Mesh(`gem_${gemId}`, targetScene);

    // Create and apply the exact vertex data from the original gem
    const vertexData = new VertexData();
    vertexData.positions = Array.from(ORIGINAL_GEM_POSITIONS);
    vertexData.indices = Array.from(ORIGINAL_GEM_INDICES);
    vertexData.normals = Array.from(ORIGINAL_GEM_NORMALS);

    // Apply the vertex data to the mesh
    vertexData.applyToMesh(gem);

    // Apply EXACT rotation from original gem (quaternion: [0.7071068286895752, 0, 0, 0.7071068286895752])
    // This is a 90-degree rotation around the X axis to match original BrilliantGem.babylon
    gem.rotationQuaternion = new Quaternion(
      0.7071068286895752,
      0,
      0,
      0.7071068286895752,
    );

    // Create PBR material with gem-like properties
    const material = this.createGemMaterial(targetScene);
    gem.material = material;

    // Store theme options in mesh metadata for later reference
    gem.metadata = {
      ...gem.metadata,
      fixedTheme: options?.fixedTheme ?? null,
      followAppTheme: options?.followAppTheme ?? false,
    };

    // Emit event for gem creation
    this.emit("gem:created", { gem, material, options });

    return gem;
  }

  /**
   * Create a PBR material with gem-like optical properties
   * @param scene The scene to create the material in
   * @returns The created PBR material
   */
  createGemMaterial(scene: Scene): PBRMaterial {
    const material = new PBRMaterial("gemMaterial", scene);
    const preset = GemManager.BRILLIANT_PRESET;

    // Default emerald theme colors
    material.albedoColor = new Color3(0.024, 0.37, 0.27); // emerald-800
    material.emissiveColor = new Color3(0.024, 0.31, 0.23); // emerald-900

    // Apply preset material properties
    material.metallic = preset.metallic;
    material.roughness = preset.roughness;
    material.alpha = preset.alpha;
    material.emissiveIntensity = preset.emissiveIntensity;

    // Apply refraction (internal transparency/clarity) using preset value
    material.subSurface.isRefractionEnabled = true;
    material.subSurface.refractionIntensity = preset.refractionIntensity;
    material.subSurface.indexOfRefraction =
      1.6 + preset.refractionIntensity * 0.8; // Dynamic based on preset
    material.subSurface.minimumThickness =
      0.5 - preset.refractionIntensity * 0.3; // Dynamic based on preset
    material.subSurface.maximumThickness =
      3.0 + preset.refractionIntensity * 4.0; // Dynamic based on preset
    material.subSurface.tintColor = material.albedoColor;

    // Additional PBR properties
    material.backFaceCulling = false; // Disable back face culling for proper transparency
    material.twoSidedLighting = true;

    // Enable iridescence using preset values
    material.iridescence.isEnabled = preset.iridescenceEnabled;
    material.iridescence.intensity = preset.iridescenceIntensity;

    // Set environment intensity for reflections from preset
    material.environmentIntensity = preset.environmentIntensity;

    // Enable depth pre-pass for proper edge rendering on transparent geometry
    // Note: InteractionManager disables this when outlineEdges is enabled
    material.needDepthPrePass = true;

    return material;
  }

  /**
   * Clear all gems from the scene
   */
  clearAll(): void {
    // Currently no internal gem tracking, method for API compatibility
    // Gems are managed by the scene itself
  }

  /**
   * Apply theme colors to a gem mesh
   * @param gem The gem mesh to apply colors to
   * @param theme The theme name
   * @param isDarkMode Whether dark mode is enabled
   */
  applyThemeColors(gem: Mesh, theme: string, isDarkMode = false): void {
    const material = gem.material as PBRMaterial;
    if (!material || !(material instanceof PBRMaterial)) {
      console.warn("Gem does not have a PBR material");
      return;
    }

    let albedoColor: Color3;
    let emissiveColor: Color3;

    // EXACT theme colors from original implementation
    switch (theme) {
      case "emerald":
        albedoColor = new Color3(0.024, 0.37, 0.27); // emerald-800
        emissiveColor = new Color3(0.024, 0.31, 0.23); // emerald-900
        break;
      case "sky":
        albedoColor = new Color3(0.012, 0.41, 0.63); // sky-700
        emissiveColor = new Color3(0.027, 0.35, 0.52); // sky-800
        break;
      case "amber":
        albedoColor = new Color3(0.96, 0.62, 0.043); // amber-500
        emissiveColor = new Color3(0.47, 0.21, 0.06); // amber-900
        break;
      case "purple":
        albedoColor = new Color3(0.58, 0.27, 0.85); // purple-700
        emissiveColor = new Color3(0.34, 0.11, 0.53); // purple-900
        break;
      default:
        // Default to emerald
        albedoColor = new Color3(0.024, 0.37, 0.27);
        emissiveColor = new Color3(0.024, 0.31, 0.23);
    }

    // Apply colors with adjustment for dark mode, respecting preset base values
    if (isDarkMode) {
      // Use preset base + 0.1 for dark mode for better visibility
      material.emissiveIntensity =
        GemManager.BRILLIANT_PRESET.emissiveIntensity + 0.1;
      material.environmentIntensity = 0.8;
    } else {
      // Use preset base value for light mode
      material.emissiveIntensity =
        GemManager.BRILLIANT_PRESET.emissiveIntensity;
      material.environmentIntensity =
        GemManager.BRILLIANT_PRESET.environmentIntensity;
    }

    material.albedoColor = albedoColor;
    material.emissiveColor = emissiveColor;

    // Emit event for theme application
    this.emit("gem:themeApplied", { gem, isDarkMode, theme });
  }

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    // GemManager doesn't need special initialization
    this.emit("manager:initialized", { manager: "GemManager" });
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this.scene = null;
  }
}
